import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Input, Button, Alert, Spin, Typography } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Logo } from '../../assets/icons/logo'
import {
  LoginLayout,
  SiteLayout,
  ContentLayout,
  LoginForm,
  HeaderContainer,
  LogoContainer,
  Title,
  StyledFormItem,
  ButtonContainer,
  Footer,
} from '../login/styles'

const layout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
}

const MIN_PASSWORD_LENGTH = 8

export default function PasswordResetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = String(searchParams.get('token') || '').trim()

  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [valid, setValid] = useState(false)
  const [login, setLogin] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    let cancelled = false
    const validate = async () => {
      if (!token) {
        setValid(false)
        setError('Ссылка недействительна или истекла')
        setChecking(false)
        return
      }
      try {
        const response = await fetch(
          `/api/password-reset/validate?token=${encodeURIComponent(token)}`,
        )
        const data = await response.json().catch(() => ({}))
        if (cancelled) return
        if (!response.ok) {
          setValid(false)
          setError(data?.errors?.[0]?.detail || 'Ссылка недействительна или истекла')
        } else {
          setValid(true)
          setLogin(data?.data?.login || null)
          setError(null)
        }
      } catch {
        if (!cancelled) {
          setValid(false)
          setError('Ошибка сети')
        }
      } finally {
        if (!cancelled) setChecking(false)
      }
    }
    validate()
    return () => {
      cancelled = true
    }
  }, [token])

  const onFinish = async (values: any) => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch('/api/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          password: values.password,
          password_confirm: values.password_confirm,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        setError(data?.errors?.[0]?.detail || 'Не удалось изменить пароль')
        return
      }
      setDone(true)
    } catch {
      setError('Ошибка сети')
    } finally {
      setLoading(false)
    }
  }

  const currentYear = () => new Date().getFullYear()

  return (
    <LoginLayout>
      <SiteLayout className="site-layout">
        <ContentLayout>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <LoginForm
              {...layout}
              name="password-reset"
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <HeaderContainer>
                <LogoContainer>
                  <Logo />
                </LogoContainer>
                <Title>Новый пароль</Title>
                {login ? (
                  <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                    Аккаунт: {login}
                  </Typography.Text>
                ) : null}
                {error ? (
                  <Alert style={{ marginTop: 16 }} type="error" showIcon message={error} />
                ) : null}
                {done ? (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="success"
                    showIcon
                    message="Пароль успешно изменён. Теперь можно войти."
                  />
                ) : null}
              </HeaderContainer>

              {checking && (
                <div style={{ textAlign: 'center', padding: 24 }}>
                  <Spin />
                </div>
              )}

              {!checking && valid && !done && (
                <>
                  <StyledFormItem
                    label="Новый пароль:"
                    name="password"
                    rules={[
                      { required: true, message: 'Введите новый пароль' },
                      {
                        min: MIN_PASSWORD_LENGTH,
                        message: `Минимум ${MIN_PASSWORD_LENGTH} символов`,
                      },
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </StyledFormItem>
                  <StyledFormItem
                    label="Подтверждение пароля:"
                    name="password_confirm"
                    dependencies={['password']}
                    rules={[
                      { required: true, message: 'Подтвердите пароль' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve()
                          }
                          return Promise.reject(new Error('Пароли не совпадают'))
                        },
                      }),
                    ]}
                  >
                    <Input.Password autoComplete="new-password" />
                  </StyledFormItem>
                  <Form.Item>
                    <ButtonContainer>
                      <Button type="primary" htmlType="submit" loading={loading}>
                        Сохранить <ArrowRightOutlined />
                      </Button>
                      <Link to="/login" style={{ fontSize: '90%' }}>
                        На страницу входа
                      </Link>
                    </ButtonContainer>
                  </Form.Item>
                </>
              )}

              {!checking && (!valid || done) && (
                <Form.Item>
                  <ButtonContainer>
                    <Button type="primary" onClick={() => navigate('/login')}>
                      На страницу входа
                    </Button>
                    {!valid && (
                      <Link to="/password-recovery" style={{ fontSize: '90%' }}>
                        Запросить новую ссылку
                      </Link>
                    )}
                  </ButtonContainer>
                </Form.Item>
              )}
            </LoginForm>
          </div>
        </ContentLayout>
        <Footer>&copy; 2020-{currentYear()} ООО «Аксиома». Все права защищены.</Footer>
      </SiteLayout>
    </LoginLayout>
  )
}
